import { addListener } from "~/lib/signals";
import { CONFIG } from "~/config";
import { _ } from "~/i18n";
import WeatherGraph from "~/weather/graph";
import { WeatherData } from "~/weather/interfaces";
import { node, icon } from "~/util/dom";
import { store } from "~/state/store";

interface IPOIData {
	title: string,
	poiType: number;
	source: string;
	id: number;
	[key: string]: any;
}

const PADDING = 16;
const IS_CELSIUS = true;//["cs", "sk", "de"].indexOf(CONFIG.defaultLang) != -1;
const MIN_PRECIP_HEIGHT = 1;
const MAX_PRECIP_HEIGHT = 28;
const RESIZE_TO = 150;
const INTERVAL_SECONDS = 3 * 3600;
const REQUEST_TIMEOUT = 15000;

const formatTemp = value => {
	const roundValue = Math.round(value);
	const showValue = IS_CELSIUS ? roundValue : Math.round(Mapy.Util.celsiusToFahreint(roundValue));
	const unit = IS_CELSIUS ? "°" : "°F";

	return `${showValue}${unit}`;
};

const formatPrecip = (precip, isSnow) => {
	const value = (precip >= 10 || precip % 1 <= 0.01 ? Math.floor(precip) : precip.toFixed(1)).toString().replace(".", ",");
	const unit = isSnow ? "cm" : "mm";

	return `${value} ${unit}`;
};

export default class Weather {
	_coords: [number, number];

	_poiData: IPOIData;

	_parent: HTMLElement;

	_dom: {
		cont?: HTMLDivElement;
		sunrise?: HTMLSpanElement;
		sunset?: HTMLSpanElement;
		days?: Array<HTMLDivElement>;
	};

	_weatherGraph: WeatherGraph;

	_data: WeatherData;

	_url: string;

	_svgCache = {};

	_resizeBind: () => void;

	_resizeID: NodeJS.Timeout;

	_windyLink: boolean;

	_isEnable: boolean;

	constructor(data: IPOIData, parent: HTMLElement) {
		this._coords = "origCoords" in data ? data.origCoords.toWGS84() : data.coords.toWGS84();
		this._poiData = data;
		this._parent = parent;
		this._dom = {};
		this._weatherGraph = null;
		this._data = null;
		this._url = `${CONFIG.requestUrl}/weather`;
		this._svgCache = {};
		this._resizeBind = this._resize.bind(this);
		this._resizeID = null;
		this._windyLink = data.weatherWindy;
		this._isEnable = false;

		// pokud neprisel z backendu priznak, ze se pocasi nema nacist -> jedem pak dal
		if (data.showWeather !== false) {
			this._isEnable = true;
			this._build();
			this._loadData();

			window.addEventListener("resize", this._resizeBind);
			addListener(this, "print-page", () => this._printPage());
			addListener(this, "print-done", () => this._printDone());
		}
	}

	destroy() {
		if (!this._isEnable) {
			return;
		}

		if (this._weatherGraph) {
			this._weatherGraph.destroy();
			this._weatherGraph = null;
		}

		this._dom.cont.remove();
		this._clearResizeTO();
		window.removeEventListener("resize", this._resizeBind);
	}

	get state() {
		const obj: {
			weather?: string;
		} = {};

		if (this._isEnable && this._dom.cont.classList.contains("opened")) {
			obj.weather = this._poiData.title.replace(" ", "-");
		}

		return obj;
	}

	_build() {
		const cont = node("div", { className: "weather" });

		this._dom.cont = cont;
		this._dom.sunrise = node("span", { className: "sunset", title: _("weather.sunrise") });
		this._dom.sunset = node("span", { className: "sunset", title: _("weather.sunset") });

		this._parent.appendChild(cont);
	}

	_loadData() {
		const loading = node("p", { className: "load", textContent: _("weather.load") });

		this._dom.cont.innerHTML = "";
		this._dom.cont.appendChild(loading);

		Mapy.Util.request(`${this._url}/forecast`, {
			get: {
				lon: this._coords[0].toFixed(5),
				lat: this._coords[1].toFixed(5),
				lang: CONFIG.defaultLang,
			},
			timeout: REQUEST_TIMEOUT,
		}).promise.then(reqObj => {
			const data: WeatherData = reqObj.data;

			// analytika
			Mapy.getComponent("logger").log("weatherDetailShow");

			loading.remove();
			this._processData(data);
			this._response(data);

			if (this._poiData.weatherShow) {
				this._toggleShow(true);
			}
		}, () => {
			loading.remove();
			this._dom.cont.classList.add("error");
			// analytika
			Mapy.getComponent("logger").log("weatherError");
		});
	}

	_processData(data: WeatherData) {
		const icons = data.stateIcons || [];
		const rainCat = data.categories?.precip || [];
		const snowCat = data.categories?.snowPrecip || [];
		const wind = data.categories?.wind || [];
		const graphRanges = data.graphRanges || null;

		// denni predpoved
		data.daily.forEach(day => {
			const svgData = icons[day.icon];

			day.my = {
				svg: this._getSVG(svgData.url, svgData.tooltip),
				temp: formatTemp(day.tempMax),
			};
		});

		// misto
		const parts = data.place.localNow.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/u);
		const dateObj = parts.length === 6 ? new Date(parseInt(parts[1]), parseInt(parts[2]) - 1, parseInt(parts[3]), parseInt(parts[4]), parseInt(parts[5])) : new Date();
		let sunriseSeconds = -1;
		let sunsetSeconds = -1;

		if (data.place.sunrise) {
			const sunriseParts = data.place.sunrise.split(":").map(item => parseInt(item));

			sunriseSeconds = (sunriseParts[0] * 3600) + (sunriseParts[1] * 60);
		}

		if (data.place.sunset) {
			const sunsetParts = data.place.sunset.split(":").map(item => parseInt(item));

			sunsetSeconds = (sunsetParts[0] * 3600) + (sunsetParts[1] * 60);
		}

		data.place.my = {
			dateObj,
			ts: Date.now(),
		};

		// aktualni teplota
		data.current.my = {
			svg: this._getSVG(icons[data.current.icon][!data.current.isDay && icons[data.current.icon].urlNight ? "urlNight" : "url"], icons[data.current.icon].tooltip),
			temp: formatTemp(data.current.temp),
		};

		// jednotlive 3 hodinove intervaly
		data.entries.forEach(entry => {
			const windData = wind[entry.windCategory];
			let precip = null;
			let isSnow = false;
			let showPrecip = false;

			if ([5, 6].indexOf(entry.precipType) !== -1) { // 5 snih a 6 vlhky snih
				showPrecip = true;
				isSnow = true;
			} else if ([1, 3, 7, 8].indexOf(entry.precipType) !== -1) { // 1 dest, 3 mrznouci dest, 7 dest se snehem, 8 kroupy
				showPrecip = true;
			}

			if (showPrecip) {
				const precipCat = isSnow ? entry.snowPrecipCategory : entry.precipCategory;

				// nezobrazujeme 0 kategorie
				if (precipCat > 0) {
					const precipData = isSnow ? snowCat[entry.snowPrecipCategory] : rainCat[entry.precipCategory];

					precip = {
						isSnow,
						value: formatPrecip(isSnow ? entry.snowPrecip : entry.precip, isSnow),
						svg: this._getSVG(precipData.icon, precipData.tooltip),
						tooltip: precipData.tooltip,
						height: isSnow
							? Math.min(Math.max(entry.snowPrecip / graphRanges.snowPrecip * MAX_PRECIP_HEIGHT, MIN_PRECIP_HEIGHT), MAX_PRECIP_HEIGHT)
							: Math.min(Math.max(entry.precip / graphRanges.precip * MAX_PRECIP_HEIGHT, MIN_PRECIP_HEIGHT), MAX_PRECIP_HEIGHT)
						,
					};
				}
			}

			const svgData = icons[entry.icon] || { urlNight: "", url: "", tooltip: "" };
			const hour = parseInt(entry.localTime);
			const entrySeconds = hour * 3600;
			let sunriseRatio = null;
			let sunsetRatio = null;

			// interval svitani
			if (sunriseSeconds >= entrySeconds && sunriseSeconds < entrySeconds + INTERVAL_SECONDS) {
				sunriseRatio = (sunriseSeconds - entrySeconds) / INTERVAL_SECONDS;
			}

			// interval zapadu slunce
			if (sunsetSeconds >= entrySeconds && sunsetSeconds < entrySeconds + INTERVAL_SECONDS) {
				sunsetRatio = (sunsetSeconds - entrySeconds) / INTERVAL_SECONDS;
			}

			entry.my = {
				// spada cely interval mezi vychod/zapad slunce?
				svg: this._getSVG(!entry.isDay && svgData.urlNight ? svgData.urlNight : svgData.url, svgData.tooltip),
				temp: formatTemp(entry.temp),
				hour,
				hourTime: `${hour}h`,
				wind: {
					value: Math.round(entry.wind),
					svg: this._getSVG(windData.icon, entry.windTooltip, svgCover => {
						const svg = svgCover.querySelector("svg");

						svg.style.transform = `rotate(${entry.windDir}deg)`;
					}),
					tooltip: entry.windTooltip,
				},
				sunriseRatio,
				sunsetRatio,
				precip,
			};
		});
	}

	_getDayInfo() {
		const dateObj = this._data.place.my.dateObj;
		const date = `${_(`detail.content.bigDays.nominativ.${dateObj.getDay()}`)} ${Mapy.Util.dateFormat(dateObj, "d. m. yyyy")}`;
		const time = Mapy.Util.dateFormat(dateObj, "hh:MM");
		let timeZone = "";
		const tzParts = this._data.place.TZoffsetFormatted.match(/([+-])(\d+):(\d+)/u);

		if (tzParts.length === 4) {
			const hours = parseInt(tzParts[2]);
			const minutes = parseInt(tzParts[3]) === 0 ? "" : `.${parseInt(tzParts[3])}`;

			// rozdilna casova zona jako u me? - pak zobrazime
			timeZone = this._data.place.TZoffsetMin === new Date().getTimezoneOffset() * -1 ? "" : `(UTC${tzParts[1]}${hours}${minutes} h)`;
		}

		return `<span>${date}, </span><strong>${time}</strong><span>${timeZone}</span>`;
	}

	_getSVG(src: string, tooltip = "", cb?: (svgConver: HTMLSpanElement) => void) {
		return () => {
			const svgCover = node("span", { className: "svg-icon-cover", title: tooltip });

			if (!(src in this._svgCache)) {
				this._svgCache[src] = new Promise(resolve => {
					Mapy.Util.request(this._url + src).promise.then(reqObj => {
						// nacetlo se svg
						resolve(reqObj.data);
					}, error => {
						// eslint-disable-next-line no-console
						console.error(error);
					});
				});
			}

			this._svgCache[src].then(data => {
				// po nacteni dat
				svgCover.innerHTML = data;

				// nejaka dalsi transformace
				if (typeof cb === "function") {
					cb(svgCover);
				}
			}, () => {});

			return svgCover;
		};
	}

	_response(data: WeatherData) {
		this._data = data;
		this._weatherGraph = new WeatherGraph(data, {
			width: this._dom.cont.offsetWidth - PADDING,
			height: 250,
			dayInd: 0,
		});

		let headline: HTMLAnchorElement | HTMLDivElement = node("div", { className: "headline" });

		if (this._poiData.source === "muni" || this._poiData.typeId === 201) {
			headline = node("a", { className: "headline", href: `${CONFIG.mapyURL}/?source=${this._poiData.source}&id=${this._poiData.id}&weather=${this._poiData.title}` });
		}

		const daily = node("div", { className: "daily" });
		const graph = node("div", { className: "graph" });
		const bottomline = node("div", { className: "bottomline" });

		// titulek
		headline.appendChild(node(this._poiData.weatherShow ? "h1" : "span", { className: "title", textContent: _("weather.title") }));
		headline.appendChild(data.current.my.svg());
		headline.appendChild(node("span", { className: "temperature", textContent: data.current.my.temp }));
		const span = node("span");

		span.appendChild(icon("dropdown"));
		headline.appendChild(span);
		headline.addEventListener("click", event => {
			event.preventDefault();
			this._toggleShow();

			// analytika
			Mapy.getComponent("logger").log("click", {
				text: this._dom.cont.classList.contains("opened") ? "pocasi_rozbaleni" : "pocasi_zbaleni",
			});
		});
		headline.appendChild(node(this._poiData.weatherShow ? "h2" : "span", { className: "day-info", innerHTML: this._getDayInfo() }));

		// denni predpovedi
		const days = [];
		const firstDayId = this._data.place.my.dateObj.getDay();

		data.daily.forEach((day, ind) => {
			// dnesek + 4 dalsi dny
			if (ind > 4) {
				return;
			}

			const oneDay = node("div", { className: "day" });

			oneDay.addEventListener("click", () => {
				this._setDay(ind);
				// analytika - proklik na jiny den
				Mapy.getComponent("logger").log("click", {
					text: "pocasi_zmena_dne",
				});
			});
			oneDay.appendChild(day.my.svg());
			oneDay.appendChild(node("span", { className: "temp", textContent: day.my.temp }));
			oneDay.appendChild(node("span", { className: "name", textContent: ind === 0 ? _("weather.today") : _(`detail.content.bigDays.nominativ.${(firstDayId + ind) % 7}`) }));
			daily.appendChild(oneDay);
			days.push(oneDay);
		});

		this._dom.days = days;

		// graf
		graph.appendChild(this._weatherGraph.container);

		// spodni radek
		if (this._data.daily[0]?.sunrise && this._data.daily[0]?.sunset) {
			this._dom.sunrise.textContent = this._data.daily[0]?.sunrise;
			this._dom.sunset.textContent = this._data.daily[0]?.sunset;

			bottomline.appendChild(this._getSVG(data.place.sunriseImg, _("weather.sunrise"))());
			bottomline.appendChild(this._dom.sunrise);
			bottomline.appendChild(this._getSVG(data.place.sunsetImg, _("weather.sunset"))());
			bottomline.appendChild(this._dom.sunset);
		} else {
			bottomline.classList.add("no-sun-values");
		}

		// pocasi - link/staticky text
		if (this._windyLink) {
			const weatherLink = node("a", { textContent: "Windy", href: this._getWindyLink(), target: "_blank" });

			/*weatherLink.addEventListener("pointerdown", () => {
				// analytika
				Mapy.getComponent("logger").log("click", {
					text: "pocasi_proklik"
				});
			});*/
			bottomline.appendChild(weatherLink);
		} else {
			bottomline.appendChild(node("span", { className: "weather-source", textContent: `${_("weather.source")} ${_("weather.linkTitle")}` }));
		}

		this._dom.cont.appendChild(headline);
		this._dom.cont.appendChild(daily);
		this._dom.cont.appendChild(graph);
		this._dom.cont.appendChild(bottomline);
	}

	_setDay(index = 0) {
		// zvyraznime
		this._dom.days.forEach((day, ind) => {
			day.classList.remove("active");

			if (ind === index) {
				day.classList.add("active");
			}
		});

		this._dom.sunrise.textContent = this._data.daily[index]?.sunrise || "";
		this._dom.sunset.textContent = this._data.daily[index]?.sunset || "";

		this._weatherGraph.update({
			dayInd: index,
		});
		this._dom.cont.classList[index === 0 ? "remove" : "add"]("first-day");

		// analytika - zobrazeni dne
		Mapy.getComponent("logger").log("weatherDayInfo", {
			dayId: index,
		});
	}

	_toggleShow(forceShow?: boolean) {
		if (forceShow) {
			this._dom.cont.classList.add("opened");
		} else {
			this._dom.cont.classList.toggle("opened");
		}

		// pri otevreni - je tam scrollbar, chceme spravnou sirku
		if (this._dom.cont.classList.contains("opened")) {
			this._setDay();
			this._weatherGraph.update({
				width: this._dom.cont.offsetWidth - PADDING,
				dayInd: 0,
			});
		}

		Mapy.getComponent("history").save(true);
	}

	_getWindyLink() {
		const lon = this._coords[0].toFixed(3);
		const lat = this._coords[1].toFixed(3);
		const { zoom } = store.getState().map;

		return `https://www.windy.com/${lat}/${lon}?${lat},${lon},${zoom}`;
	}

	_resize(opts = {}) {
		this._clearResizeTO();
		this._resizeID = setTimeout(() => {
			this._resizeID = null;

			if (this._weatherGraph) {
				this._weatherGraph.update({
					width: this._dom.cont.offsetWidth - PADDING,
					...opts,
				});
			}
		}, RESIZE_TO);
	}

	_clearResizeTO() {
		if (this._resizeID) {
			clearTimeout(this._resizeID);
			this._resizeID = null;
		}
	}

	_printPage() {
		if (!this._dom.cont.classList.contains("opened") || !this._weatherGraph) {
			return;
		}

		this._weatherGraph.update({
			width: 700,
			dayInd: this._dom.days.findIndex(day => day.classList.contains("active")),
			move: true,
		});
	}

	_printDone() {
		if (!this._dom.cont.classList.contains("opened") || !this._weatherGraph) {
			return;
		}
		this._resize({
			dayInd: this._dom.days.findIndex(day => day.classList.contains("active")),
			move: true,
		});
	}
}
